import React, { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";

export const CAPTCHA_LENGTH = 5;
const CHARSET = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";

const generateCode = (length = CAPTCHA_LENGTH) => {
  let code = "";
  for (let i = 0; i < length; i++) {
    code += CHARSET.charAt(Math.floor(Math.random() * CHARSET.length));
  }
  return code;
};

export const getCaptchaValidation = (value, captchaCode) => {
  const trimmed = (value || "").trim();

  if (!trimmed) {
    return { valid: false, message: "Please enter the captcha" };
  }
  if (trimmed.length !== CAPTCHA_LENGTH) {
    return { valid: false, message: `Captcha must be ${CAPTCHA_LENGTH} characters` };
  }
  if (trimmed !== captchaCode) {
    return { valid: false, message: "Captcha does not match" };
  }
  return { valid: true, message: "" };
};

const Captcha = forwardRef(({ value = "", onChange = () => {}, onCodeChange = () => {}, error = "" }, ref) => {
  const canvasRef = useRef(null);
  const [captchaCode, setCaptchaCode] = useState(() => generateCode());

  const drawCaptcha = useCallback((code) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);

    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = `rgba(0, 0, 0, ${Math.random() * 0.15})`;
      ctx.beginPath();
      ctx.arc(Math.random() * width, Math.random() * height, Math.random() * 1.5, 0, Math.PI * 2);
      ctx.fill();
    }

    const charWidth = width / (code.length + 1);
    ctx.textBaseline = "middle";

    for (let i = 0; i < code.length; i++) {
      const x = charWidth * (i + 0.7);
      const y = height / 2 + (Math.random() * 10 - 5);
      const angle = (Math.random() * 30 - 15) * (Math.PI / 180);
      const fontSize = 26 + Math.floor(Math.random() * 6);

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.font = `bold ${fontSize}px Georgia, serif`;
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#111";
      ctx.fillStyle = "#1a1a1a";
      ctx.strokeText(code[i], 0, 0);
      ctx.fillText(code[i], 0, 0);
      ctx.restore();
    }

    ctx.beginPath();
    ctx.strokeStyle = "#4A7C59";
    ctx.lineWidth = 2;
    const startY = height / 2 + (Math.random() * 8 - 4);
    ctx.moveTo(8, startY);
    for (let x = 8; x < width - 8; x += 8) {
      const y = startY + Math.sin(x / 12) * 6;
      ctx.lineTo(x, y);
    }
    ctx.stroke();

    for (let i = 0; i < 2; i++) {
      ctx.beginPath();
      ctx.strokeStyle = `rgba(0, 0, 0, ${0.1 + Math.random() * 0.15})`;
      ctx.lineWidth = 1;
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.lineTo(Math.random() * width, Math.random() * height);
      ctx.stroke();
    }
  }, []);

  const refreshCaptcha = useCallback(() => {
    const next = generateCode();
    setCaptchaCode(next);
    onChange({ target: { name: "captcha", value: "" } });
  }, [onChange]);

  useEffect(() => {
    drawCaptcha(captchaCode);
    onCodeChange(captchaCode);
  }, [captchaCode, drawCaptcha, onCodeChange]);

  useImperativeHandle(ref, () => ({
    validate: () => getCaptchaValidation(value, captchaCode),
    refresh: refreshCaptcha,
    getCode: () => captchaCode,
  }));

  return (
    <div style={{ width: "100%", marginBottom: "16px" }}>
      <label
        htmlFor="captcha"
        style={{
          display: "block",
          fontSize: "14px",
          fontWeight: "600",
          color: "#2D5A27",
          marginBottom: "8px",
          textAlign: "left",
        }}
      >
        Enter Captcha <span style={{ color: "red" }}>*</span>
      </label>

      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          borderRadius: "10px",
          overflow: "hidden",
          border: "1px solid #d1d5db",
          marginBottom: "10px",
          height: "52px",
        }}
      >
        <div
          style={{
            flex: 1,
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 0,
          }}
        >
          <canvas
            ref={canvasRef}
            width={200}
            height={50}
            style={{ width: "100%", height: "100%", display: "block" }}
            aria-label="Captcha image"
          />
        </div>
        <button
          type="button"
          onClick={refreshCaptcha}
          title="Refresh captcha"
          aria-label="Refresh captcha"
          style={{
            width: "52px",
            border: "none",
            borderLeft: "1px solid #d1d5db",
            background: "#4b5563",
            color: "#e5e7eb",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon icon={faSyncAlt} />
        </button>
      </div>

      <input
        id="captcha"
        name="captcha"
        type="text"
        value={value}
        onChange={onChange}
        placeholder="Enter the captcha"
        autoComplete="off"
        maxLength={CAPTCHA_LENGTH}
        style={{
          width: "100%",
          height: "48px",
          borderRadius: "10px",
          border: error ? "1px solid #ef4444" : "1px solid #d1d5db",
          padding: "0 14px",
          fontSize: "15px",
          fontFamily: "Lato, sans-serif",
          outline: "none",
          boxSizing: "border-box",
          color: "#1f2937",
        }}
      />
      {error && (
        <p style={{ color: "red", fontSize: "12px", marginTop: "6px", textAlign: "left" }}>
          {error}
        </p>
      )}
    </div>
  );
});

Captcha.displayName = "Captcha";

export default Captcha;
